<script setup lang="ts">
import { ref } from 'vue'

interface GuestSlot {
  id: string
  slotId: string
  offerCode: string
  status: 'pending' | 'connected'
  createdAt: number
}

const props = defineProps<{
  slot: GuestSlot
  onShowQR: (slot: GuestSlot) => void
  onCopyCode: (slotId: string) => void
  onDelete: (slotId: string) => void
}>()

const longTapActivated = ref(false)
const tapTimeout = ref<NodeJS.Timeout | null>(null)

const handleDelete = () => {
  if (confirm('¿Eliminar este hueco de jugador?')) {
    props.onDelete(props.slot.id)
  }
}

const onTap = () => {
  // Esperar un momento para verificar si no fue un longtap
  tapTimeout.value = setTimeout(() => {
    if (!longTapActivated.value) {
      props.onShowQR(props.slot)
    }
    longTapActivated.value = false
  }, 100)
}

const onLongTap = () => {
  longTapActivated.value = true
  
  // Limpiar timeout de tap si existe
  if (tapTimeout.value) {
    clearTimeout(tapTimeout.value)
    tapTimeout.value = null
  }
  
  props.onCopyCode(props.slot.id)
  
  // Reset después de un momento
  setTimeout(() => {
    longTapActivated.value = false
  }, 600)
}
</script>

<template>
  <div
    v-touch:tap="onTap"
    v-touch:longtap="onLongTap"
    v-touch-options="{
      longTapTimeInterval: 500,
      touchClass: 'holding'
    }"
    title="Click para ver QR | Mantén presionado para copiar código"
    class="slot-item"
  >
    <span class="text-2xl">📱</span>
    <span class="text-white flex-1">Esperando jugador...</span>
    <button 
      @click.prevent.stop="handleDelete"
      @touchstart.prevent.stop
      @touchend.prevent.stop
      class="text-red-400 hover:text-red-300 text-xl font-bold px-2 py-1 rounded hover:bg-red-500/20 transition-all"
      title="Eliminar hueco"
    >
      ✕
    </button>
    <span class="text-xl action-hint">👆</span>
  </div>
</template>

<style scoped>
.slot-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(20, 184, 166, 0.2);
  border-radius: 0.5rem;
  padding: 0.75rem;
  border: 2px dashed rgba(20, 184, 166, 0.5);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  animation: subtle-pulse 2s ease-in-out infinite;
}

.slot-item:hover {
  background: rgba(20, 184, 166, 0.3);
  transform: scale(1.02);
}

.action-hint {
  animation: bounce 1.5s ease-in-out infinite;
}

@keyframes subtle-pulse {
  0%, 100% { 
    opacity: 0.9; 
    transform: scale(1); 
  }
  50% { 
    opacity: 1; 
    transform: scale(1.01); 
  }
}

@keyframes bounce {
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-3px); 
  }
}
</style>